import Select from 'react-select';
import { editableSelectStyle } from "../../styles/react-select";
import CreatableSelect from 'react-select/creatable';
import { FixedSizeList } from 'react-window';
import { ClientRadio } from "../inputs/ClientRadio";
import { NonInputFieldLabelWrapper } from "./NonFunctionalFieldLabel";
import { slugify } from "../../lib/utils";
import { resolveValue } from "../../lib/fields/SingleChoice/validity";
import { useReferenceDataOptions } from "../../lib/customHooks/useReferenceData";
import { withDependentReferenceDataOptions } from "../../lib/customHooks/useReferenceData";
import { clientTypeSelector } from "../../store/selectors/clientSettings";
import { useSelector } from 'react-redux';
const filterEntryValues = ({
  selectedOption,
  selectedValue,
  removeDuplicates,
  currentlySelectedOptions
}) => {
  let selectedIndex = selectedOption > -1 ? selectedOption : -1;
  const isSelected = (text, position) => {
    const optionSelected = selectedIndex > -1;
    if (optionSelected) {
      return selectedIndex === position;
    }
    if (!optionSelected && selectedValue === text) {
      selectedIndex = position;
      return true;
    }
  };
  return (acc, {
    text,
    position
  }) => {
    if (text) {
      const selected = isSelected(text, position);
      if (!removeDuplicates || !currentlySelectedOptions.includes(text)) {
        acc.push({
          selected,
          position,
          value: text,
          label: text
        });
      }
    }
    return acc;
  };
};
export const RadioButtonsPreview = ({
  guid,
  update,
  options = []
}) => {
  return <div className="preview--radio-buttons--wrapper">
      {!!options.length && options.map((entry, i) => {
      const {
        value,
        selected,
        position,
        label
      } = entry;
      const choiceID = `${slugify(value)}-${position}-${guid}`;
      return <ClientRadio key={`radio-button-${i}`} id={choiceID} label={label} selected={selected && value} value={value} onChange={(checked, optionValue) => checked && update(optionValue, {
        selectedOption: position
      })} />;
    })}
    </div>;
};
const BoxesPreview = ({
  update,
  options = []
}) => {
  return <div className="preview--boxes--wrapper">
      {!!options.length ? options.map((entry, i) => {
      const {
        value,
        selected,
        position,
        label
      } = entry;
      const choiceID = `${slugify(value)}-${position}`;
      return <div key={i} id={choiceID} className={`${selected ? 'selected' : ''}`} selected={selected} onClick={() => {
        update(value, {
          selectedOption: position
        });
      }}>
                {label}
              </div>;
    }) : ''}
    </div>;
};

// uses the FixedSizeList component to render the dropdown menu, which uses virtualization to drastically reduce the amount of
// time it takes for the dropdown menu to open and significantly improves the scrolling experience when there are 1k+ menu options
const DropDownMenu = ({
  optionsLength,
  optionsLimit,
  focusedOption = {},
  children
}) => {
  //* Ternary included for optionHeight to add height if single choice dropdown (editable = 1 optionsLength and read only = 0 optionsLength) is empty
  const optionHeight = optionsLength > 1 ? 40 : 50;
  const {
    position = 0
  } = focusedOption;
  const maxOptionsToShow = optionsLength > optionsLimit ? optionsLimit : optionsLength;
  const menuHeight = optionsLength === 0 ? optionHeight : optionHeight * maxOptionsToShow;
  const initialOffset = position * optionHeight;
  // Since position is a zero index based and maxOptionsToShow is length we need to
  // do this transform to make them an apples to apples comparison.
  const optionOffInitialRender = position + 1 >= maxOptionsToShow;
  //* Transform necessary to include empty single choice dropdown "No Options" default messaging
  //* empty single choice comes in as an object otherwise and react-select noOptionsMessage (default) will not show
  const items = Array.isArray(children) ? children : [children];
  return <FixedSizeList height={menuHeight} itemCount={items.length} itemSize={optionHeight} initialScrollOffset={optionOffInitialRender && initialOffset}>
      {({
      index,
      style
    }) => <div style={style}>{items[index]}</div>}
    </FixedSizeList>;
};
const DropDownPreview = withDependentReferenceDataOptions(({
  update,
  options = [],
  selectedOption,
  selectedValue,
  style,
  errors,
  isDisabledInDispatch
}) => {
  const isEditablePreview = style == 34 || false;
  const readOnlyMaxOptionsLength = 5;
  const editableMaxOptionsLength = 6;
  const userValue = selectedValue ? {
    value: selectedValue,
    label: selectedValue
  } : null;
  const value = options[selectedOption] || userValue;
  const selectProps = {
    styles: editableSelectStyle,
    value,
    className: `${errors ? 'react-select__invalid invalid' : ''} select--wrapper`,
    classNamePrefix: 'react-select',
    menuPosition: 'fixed',
    openMenuOnFocus: true,
    closeMenuOnScroll: event => {
      const {
        target
      } = event;
      // different builders/webforms have different scroll containers
      return target === document || target.classList.contains('web-form--container') || target.classList.contains('preview--section') || target.classList.contains('full-screen-preview--container');
    },
    menuPortalTarget: document.querySelector('main'),
    onChange: ({
      value
    }) => {
      update(value);
    },
    components: {
      MenuList: props => <DropDownMenu {...props} optionsLimit={isEditablePreview ? editableMaxOptionsLength : readOnlyMaxOptionsLength} optionsLength={isEditablePreview ? options.length + 1 : options.length} />
    }
  };
  return <div className="foundry--dropdowns editable--drop-down-container user-form--dropdown">
        {isEditablePreview ? <CreatableSelect {...selectProps} options={[{
      label: 'Type to Add New Value'
    }, ...options]} formatCreateLabel={userInput => `Add "${userInput}"`} /> : <Select {...selectProps} isDisabled={isDisabledInDispatch} options={options} />}
      </div>;
});
const whichSingleChoiceComponent = ({
  style
}) => {
  switch (`${style}`) {
    case '1':
      return RadioButtonsPreview;
    case '0':
    case '3':
    case '34':
    case '4':
      return DropDownPreview;
    default:
      return BoxesPreview;
  }
};
export const SingleChoicePreview = previewEntry => {
  const {
    label,
    errors,
    required,
    selectedOption,
    reference_data_id,
    reference_data_column,
    entry_values: default_options,
    value,
    default: defaultValue,
    removeDuplicates = false,
    currentlySelectedOptions = [],
    style
  } = previewEntry;
  const SingleChoiceComponent = whichSingleChoiceComponent(previewEntry);
  const {
    options
  } = useReferenceDataOptions({
    reference_data_id,
    reference_data_column,
    default_options,
    removeDuplicates,
    currentlySelectedOptions
  });
  const selectedValue = resolveValue(value || defaultValue, options);
  const filteredOptions = options.reduce(filterEntryValues({
    selectedOption,
    selectedValue,
    removeDuplicates,
    currentlySelectedOptions
  }), []);
  const initialOption = filteredOptions.findIndex(({
    selected
  }) => selected);
  const location = useSelector(clientTypeSelector);
  //* single-choice dropdowns are disabled in dispatch when the entry (style 0 or 4) is associated with ref data that contains values for that entry
  const isDisabledInDispatch = (style === 0 || style === 4) && location === 'dispatch' && previewEntry.reference_data_id != null && !!options.length;
  const tooltip = `Disable 'Show Duplicates' in the form builder to enable reference data`;
  return <div className="preview--wrapper">
      <NonInputFieldLabelWrapper label={label} required={required} error={errors} errorText={errors} tooltip={isDisabledInDispatch ? tooltip : false} tooltipDirection={'bottom'}>
        <SingleChoiceComponent {...previewEntry} options={filteredOptions} selectedOption={selectedOption || initialOption} selectedValue={selectedValue} isDisabledInDispatch={isDisabledInDispatch} />
      </NonInputFieldLabelWrapper>
    </div>;
};